import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { AdminRoute, ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { TextSkeleton } from '@/components/ui/Skeleton';
import { routes } from '@/lib/routes';
import { useSessionStatus, useSessionStore } from '@/store/sessionStore';

// Lazy en todas las rutas: el bundle inicial sólo carga el shell y la home.
const HomePage = lazy(() => import('@/features/home/HomePage'));
const LandingPage = lazy(() => import('@/features/landing/LandingPage'));
const SearchPage = lazy(() => import('@/features/search/SearchPage'));
const FandomPage = lazy(() => import('@/features/fandom/FandomPage'));
const FandomCatalogPage = lazy(() => import('@/features/fandom/FandomCatalogPage'));
const StoryDetailPage = lazy(() => import('@/features/story/StoryDetailPage'));
const StoryByIdRedirect = lazy(() => import('@/features/story/StoryByIdRedirect'));
const ReaderPage = lazy(() => import('@/features/reader/ReaderPage'));
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage'));
const SettingsPage = lazy(() => import('@/features/profile/SettingsPage'));
const LibraryPage = lazy(() => import('@/features/library/LibraryPage'));
const BoardPage = lazy(() => import('@/features/board/BoardPage'));
const ArtGalleryPage = lazy(() => import('@/features/art/ArtGalleryPage'));
const ArtworkPage = lazy(() => import('@/features/art/ArtworkPage'));
const ArtUploadPage = lazy(() => import('@/features/art/ArtUploadPage'));
const ThreadPage = lazy(() => import('@/features/board/ThreadPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const NewStoryPage = lazy(() => import('@/features/dashboard/NewStoryPage'));
const StoryEditorPage = lazy(() => import('@/features/dashboard/StoryEditorPage'));
const ChapterEditorPage = lazy(() => import('@/features/dashboard/ChapterEditorPage'));
const AdminPage = lazy(() => import('@/features/admin/AdminPage'));
const SignInPage = lazy(() => import('@/features/auth/SignInPage'));
const OnboardingPage = lazy(() => import('@/features/auth/OnboardingPage'));
const LegalPage = lazy(() => import('@/features/legal/LegalPage'));
const NotFoundPage = lazy(() => import('@/features/errors/NotFoundPage'));

export default function App() {
  const init = useSessionStore((s) => s.init);

  useEffect(() => init(), [init]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Pantallas sin el shell: ocupan todo el alto. */}
          <Route path={routes.signIn} element={<SignInPage />} />
          <Route path={routes.onboarding} element={<OnboardingPage />} />
          <Route path="/:handle/:slug/:chapterIndex" element={<ReaderPage />} />

          {/*
            La raíz se bifurca: quien no tiene sesión ve la portada, que es
            una superficie de marca con su propio layout; quien ya entró va
            directo al feed, porque ya no necesita que le expliquen qué es esto.
          */}
          <Route path="/" element={<RootRoute />} />

          <Route element={<AppShell />}>
            <Route path={routes.search} element={<SearchPage />} />
            {/* El tablón se lee sin sesión; publicar y responder exige entrar. */}
            <Route path={routes.board} element={<BoardPage />} />
            <Route path="/tablon/:threadId" element={<ThreadPage />} />
            {/* La galería se mira sin cuenta; subir exige entrar. */}
            <Route path={routes.art} element={<ArtGalleryPage />} />
            <Route path="/galeria/:artworkId" element={<ArtworkPage />} />
            <Route path={routes.fandoms} element={<FandomCatalogPage />} />
            <Route path="/fandom/:slug" element={<FandomPage />} />
            {/*
              Puente para las notificaciones viejas, que guardaron una ruta que
              nunca existió. Ver StoryByIdRedirect.
            */}
            <Route path="/historia/:storyId" element={<StoryByIdRedirect />} />
            {/*
              Va antes de /:handle/:slug. React Router prioriza el segmento
              literal sobre el dinámico igual, pero el orden lo deja explícito:
              /legal/terminos no es el perfil de alguien llamado "legal".
            */}
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/:handle" element={<ProfilePage />} />
            <Route path="/:handle/:slug" element={<StoryDetailPage />} />

            <Route element={<ProtectedRoute />}>
              <Route path={routes.artUpload} element={<ArtUploadPage />} />
              <Route path={routes.settings} element={<SettingsPage />} />
              <Route path={routes.library} element={<LibraryPage />} />
              <Route path={routes.notifications} element={<NotificationsPage />} />
              <Route path={routes.dashboard} element={<DashboardPage />} />
              <Route path={routes.newStory} element={<NewStoryPage />} />
              <Route path="/panel/historia/:storyId" element={<StoryEditorPage />} />
              <Route
                path="/panel/historia/:storyId/capitulo/:chapterId"
                element={<ChapterEditorPage />}
              />
            </Route>

            <Route element={<AdminRoute />}>
              <Route path={routes.admin} element={<AdminPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * Sin sesión, la raíz es la portada pública, con su propio layout de marca.
 * Con sesión, es el feed dentro del caparazón de la app.
 */
function RootRoute() {
  const status = useSessionStatus();

  if (status === 'loading') return <RouteFallback />;
  if (status === 'anon') return <LandingPage />;

  return (
    <AppShell>
      <HomePage />
    </AppShell>
  );
}

function RouteFallback() {
  return (
    <div className="mx-auto max-w-shell px-4 py-10">
      <TextSkeleton lines={5} />
    </div>
  );
}
